/**
 * 系统管理后台外壳（见方案 2B）。
 *
 * 与家长/学生端的 AppShell **刻意长得不一样**：
 *  - 深色顶栏 + 醒目的提示条，让人一眼知道"这不是家庭界面"
 *  - 提示条写明"仅限管理员账号登录 / 不会显示任何家庭数据"，避免误操作
 *  - 没有"孩子 / 共享 / 奖品"等家庭入口：管理员根本没有那些权限
 *
 * ⚠️ 唯一**刻意与家长端共用**的东西是「构建于 …」这一行版本标记
 *    （`./BuildStamp`，文案在 `lib/buildStamp.ts`）—— 它不属于任何一端的业务，
 *    且"当前跑的是哪个构建"在两端必须是同一个答案。
 *
 * 目的很实际：管理员本来也是家里的家长，两个界面长得像就会点错。
 */
import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useAuth } from '../../lib/auth';
import { BuildStamp } from './BuildStamp';

export interface AdminNavItem {
  to: string;
  label: string;
  icon: string;
  end?: boolean;
}

/** 管理后台导航（家长/学生端导航里**不出现**这些入口）。 */
export const ADMIN_NAV: AdminNavItem[] = [
  { to: '/admin', label: '概览', icon: '📊', end: true },
  { to: '/admin/users', label: '账号', icon: '👥' },
  /**
   * 「听写字表」（2026-10-16 新增）。
   *
   * 放在「系统设置」**前面**、紧挨「账号」：它和账号管理一样是**内容管理**
   * （站点上的资产由管理员维护），而「系统设置 / 审计日志 / 邮件自检」
   * 是运行层面的东西。用户的原话是"录入维护这一块应该由系统管理员去做，
   * 应该放到系统管理员那里去" —— 所以它必须在管理后台的**一级导航**里，
   * 而不是藏在某个二级页面（藏起来等于家长还得自己录，那就白改了）。
   */
  { to: '/admin/dictation', label: '听写字表', icon: '📖' },
  { to: '/admin/settings', label: '系统设置', icon: '⚙️' },
  { to: '/admin/audit', label: '审计日志', icon: '🧾' },
  { to: '/admin/mail', label: '邮件自检', icon: '✉️' },
];

export function AdminShell({
  children,
  title,
  description,
  action,
}: {
  children: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/*
        顶部橙色提示条 2026-11-04 已按用户要求**删除**（"顶部'系统管理后台……'这块不要"）。
        它在手机上占掉一整行、把真正的内容往下推，信息量也不大 ——
        深色顶栏里那行「🛠 家庭打卡 · 系统管理」已经足够说明这是后台。

        ⚠️ 别把"管理后台"的判断重新塞回视觉横幅：权限本来由 `RequireAuth role="admin"`
        在路由层保证，界面不需要再喊一遍。

        （历史备注，保留以便追溯）这里**不再**写"仅经 SSH 隧道访问"。2026-10-16 实测：
        线上 caddy 跑的是旧配置，/admin 与 /api/v1/admin/* 从公网确实可达（200 / 401），
        所以那句话与事实不符。网络来源属于运维事实，写进 docs/RUNBOOK.md §5.4。
      */}

      <header className="bg-slate-900 text-white">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-3 px-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-base font-bold">🛠 家庭打卡 · 系统管理</p>
            <p className="truncate text-xs text-slate-300">
              {user?.nickname ?? '未知'}（{user?.username ?? '-'}）· 已绑定动态口令
            </p>
            {/*
              「构建于 …」版本标记（2026-10-16 补）。

              为什么管理后台也必须看得见：全站一直靠"强刷后看构建于变了没"确认
              用户是否加载到新构建，而管理后台此前**看不到**这一行 —— 恰恰在最需要
              确认版本的地方没法确认。它与家长端是**同一个** `BuildStamp`、**同一个**
              `__BUILD_TIME__`（见 lib/buildStamp.ts），所以要么同时有值、要么同时
              显示「—」，不会一边有一边空。

              位置：深色顶栏里、账号那一行的下面 —— 与家长端手机端头部（标题/问候语
              下面那行）完全同形，且每一页管理页面都带着，不必滚到页脚。
              颜色沿用 BuildStamp 的中性灰 `text-slate-400`（在 `bg-slate-900` 上
              对比度约 6:1，够看），于是不必在这里另传一套颜色。

              ⚠️ 版本号**不是家庭数据**，不违反"管理后台不显示任何家庭数据"这条既有
                 约束（见上面那条琥珀色提示条）；但别顺手把账号之外的别的信息加进来。
              ⚠️ 窄屏：`truncate` + 外层 `min-w-0`、右侧按钮列 `shrink-0`，与头部
                 既有的收缩约定一致（不要给它加 `flex-1`）。
            */}
            <BuildStamp />
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <NavLink
              to="/"
              className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-300 hover:bg-slate-800"
            >
              返回家庭端首页
            </NavLink>
            <button
              type="button"
              onClick={onLogout}
              className="rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-700"
            >
              退出登录
            </button>
          </div>
        </div>

        <nav aria-label="管理导航" className="mx-auto flex w-full max-w-7xl gap-1 overflow-x-auto px-2 pb-1">
          {ADMIN_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'flex shrink-0 items-center gap-1.5 rounded-t-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive ? 'bg-slate-100 text-slate-900' : 'text-slate-300 hover:bg-slate-800',
                )
              }
            >
              <span aria-hidden="true">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="mx-auto w-full max-w-7xl px-4 py-5">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-900">{title}</h1>
            {description && <p className="mt-0.5 text-sm text-slate-500">{description}</p>}
          </div>
          {action}
        </div>
        {children}
      </main>
    </div>
  );
}
